// HCT-3 — Hierarchical Convergence Through Triple-Cycle Computation.
// A deterministic analytical SIMULATOR. All values are synthetic demo telemetry.

export const HCT3_DEFAULTS = {
  raw_drift: 2.14, // degrees
  corrected_offset: 0.29, // degrees
  refractive_index: 1.0003,
  latency: 42, // ms
  jitter: 3.5, // ms
  confidence: 0.86, // 0..1
  baseline_difference: 1.85,
  temporal_delta: 120, // ms
};

// Deterministic pseudo-noise (no randomness) so results are reproducible.
function noise(i) {
  return Math.sin(i * 1.7) * 0.06 + Math.cos(i * 0.9) * 0.04;
}

// Cycle 1 Observation, Cycle 2 Correction, Cycle 3 Convergence.
export function runHct3(inputs, thresholds = { warning: 50, critical: 75 }) {
  const cfg = { ...HCT3_DEFAULTS, ...inputs };
  const steps = 12;
  const target = cfg.corrected_offset;
  const series = [];

  for (let i = 0; i < steps; i++) {
    const raw = cfg.raw_drift + noise(i) * cfg.raw_drift;
    // Correction converges from raw toward the corrected offset target.
    const corrected = target + (cfg.raw_drift - target) * Math.pow(0.68, i) + noise(i) * 0.05;
    const convergence = Math.max(0, Math.min(100, (1 - Math.abs(corrected - target) / Math.max(cfg.raw_drift - target, 0.0001)) * 100));
    const anomaly = anomalyForStep(corrected, target, cfg);
    series.push({
      step: i + 1,
      raw: Number(raw.toFixed(4)),
      corrected: Number(corrected.toFixed(4)),
      convergence: Number(convergence.toFixed(1)),
      anomaly: Number(anomaly.toFixed(1)),
    });
  }

  const last = series[series.length - 1];
  const cycle1 = {
    label: "Cycle 1 — Observation",
    baseline_raw_drift: cfg.raw_drift,
    refractive_index: cfg.refractive_index,
    latency: cfg.latency,
    jitter: cfg.jitter,
    measurements_captured: steps,
  };
  const cycle2 = {
    label: "Cycle 2 — Correction",
    baseline_difference: Number((cfg.raw_drift - target).toFixed(4)),
    corrected_offset: target,
    temporal_delta: cfg.temporal_delta,
    correction_applied: true,
  };
  const convergenceReached = last.convergence >= 95;
  const cycle3 = {
    label: "Cycle 3 — Convergence",
    corrected_value: last.corrected,
    convergence_pct: last.convergence,
    confidence: Number((cfg.confidence * (last.convergence / 100)).toFixed(3)),
    threshold_exceeded: last.anomaly >= thresholds.critical,
    convergence_reached: convergenceReached,
  };

  return {
    cfg,
    series,
    cycle1,
    cycle2,
    cycle3,
    indicators: {
      l8_drift_trigger: cfg.raw_drift >= 1.0,
      l24_anchor_match: convergenceReached,
      convergence_status: convergenceReached ? "CONVERGED" : "CONVERGING",
      anomaly_threshold: last.anomaly >= thresholds.critical ? "EXCEEDED" : last.anomaly >= thresholds.warning ? "APPROACHING" : "WITHIN LIMITS",
      reconstruction_available: convergenceReached,
    },
  };
}

function anomalyForStep(corrected, target, cfg) {
  const driftComp = Math.min(40, Math.abs(corrected - target) * 20);
  const jitterComp = Math.min(20, cfg.jitter * 2);
  const latencyComp = Math.min(20, (cfg.latency / 100) * 20);
  const confComp = (1 - cfg.confidence) * 20;
  return driftComp + jitterComp + latencyComp + confComp;
}

// ===== Live telemetry stream (locally generated; NOT external sensor data) =====
export function seedLive(cfg = HCT3_DEFAULTS) {
  return { step: 0, cfg: { ...HCT3_DEFAULTS, ...cfg } };
}

// Produce the next live sample. Uses deterministic decay + small local noise.
export function nextLiveSample(state, thresholds = { warning: 50, critical: 75 }) {
  const cfg = state.cfg;
  const step = state.step + 1;
  const target = cfg.corrected_offset;
  const n = () => (Math.random() - 0.5); // local jitter only
  const raw = Math.max(0, cfg.raw_drift + Math.sin(step * 0.5) * 0.12 + n() * 0.08);
  const decay = Math.pow(0.82, step % 14);
  const corrected = Math.max(0, target + (cfg.raw_drift - target) * decay + n() * 0.04);
  const latency = Math.max(1, cfg.latency + Math.sin(step * 0.9) * 6 + n() * 3);
  const jitter = Math.max(0, cfg.jitter + Math.cos(step * 1.3) * 1.2 + n() * 0.6);
  const spread = Math.max(cfg.raw_drift - target, 0.0001);
  const convergence = Math.max(0, Math.min(100, (1 - Math.abs(corrected - target) / spread) * 100));
  const confidence = Math.max(0, Math.min(1, cfg.confidence * (0.6 + 0.4 * (convergence / 100))));
  const anomaly = Number(
    anomalyForStep(corrected, target, { ...cfg, latency, jitter, confidence }).toFixed(1)
  );
  const sample = {
    step,
    t: new Date().toISOString(),
    raw_drift: Number(raw.toFixed(4)),
    corrected_offset: Number(corrected.toFixed(4)),
    refractive_index_v: cfg.refractive_index,
    latency: Number(latency.toFixed(1)),
    jitter: Number(jitter.toFixed(2)),
    confidence: Number(confidence.toFixed(3)),
    baseline_difference: Number((cfg.raw_drift - target).toFixed(4)),
    temporal_delta: cfg.temporal_delta,
    anomaly_score: anomaly,
    convergence: Number(convergence.toFixed(1)),
  };
  const indicators = {
    l8_drift_trigger: raw >= 1.0,
    l24_anchor_match: convergence >= 95,
    convergence_status: convergence >= 95 ? "CONVERGED" : "CONVERGING",
    anomaly_threshold: anomaly >= thresholds.critical ? "EXCEEDED" : anomaly >= thresholds.warning ? "APPROACHING" : "WITHIN LIMITS",
    integrity_status: "OK",
  };
  return { state: { ...state, step }, sample, indicators };
}
